import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getBrandConfig } from '@/lib/configuration/configuration';
import { getContactDetailsFallbackInfo } from '@/lib/const';
import { messages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';

interface ContactInfo {
  email: string;
  phoneNumber: string;
  weekdayHours: string;
  weekendHours: string;
}

const contactInfoCache = new Map<string, Promise<ContactInfo>>();

export const clearContactInfoCache = (): void => {
  contactInfoCache.clear();
};

const fetchContactInfoRaw = (
  countryCode: string,
  draftMode: boolean,
): Promise<ContactInfo> => {
  const cacheKey = `${countryCode}:${draftMode}`;
  const cached = contactInfoCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const request = (async () => {
    const response = await fetch(
      `/api/contact-center/?countryCode=${countryCode}&draftMode=${draftMode}`,
    );

    if (!response.ok) {
      throw new Error('contact-center request failed');
    }

    return response.json() as Promise<ContactInfo>;
  })();

  contactInfoCache.set(cacheKey, request);
  void request.catch(() => {
    contactInfoCache.delete(cacheKey);
  });

  return request;
};

export const useContactInfo = () => {
  const { formatMessage } = useIntl();
  const { country } = useRegionProvider();
  const draftMode = useContext(DraftModeContext);

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phoneNumber: '',
    weekdayHours: '',
    weekendHours: '',
  });

  const fetchContactInfo = async (countryCode: string) => {
    if (getBrandConfig().disableAlgoliaApiCall) return;
    try {
      const data = await fetchContactInfoRaw(countryCode, draftMode);

      const translateHours = (hours: string) => {
        return hours
          .replace('Mon-Fri', formatMessage(commonMessages.mondayToFriday))
          .replace('Saturday', formatMessage(commonMessages.saturday))
          .replace('Sunday', formatMessage(commonMessages.sunday))
          .replace('Sat - Sun', formatMessage(commonMessages.saturdayToSunday))
          .replaceAll('closed', formatMessage(messages.closed))
          .replace('open', formatMessage(messages.open));
      };

      setContactInfo({
        email: data.email,
        phoneNumber: data.phoneNumber,
        weekdayHours: translateHours(data.weekdayHours),
        weekendHours: translateHours(data.weekendHours),
      });
    } catch (err) {
      setContactInfo(getContactDetailsFallbackInfo());
    }
  };

  useEffect(() => {
    if (country.code) {
      fetchContactInfo(country.code);
    }
  }, [country.code]);

  return contactInfo;
};
